import React, { ReactNode, createContext, useContext, useMemo, useState } from 'react'
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone'
import { Outlet } from 'react-router-dom'
import { unikId } from 'unik-id'

import { getFirstClassificationResult } from '@frame/ui-components/document-card/utils'

import { useDocumentsFlowContext } from '@documents/contexts/documents-flow-context'
import {
  ClassificationResult,
  DocumentTypeOption,
  ErrorCode,
  FileType,
  FileUploadState,
  TrackedFile,
} from '@documents/types'
import { UploadAndClassifyFileResponse, UploadFileError } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'

import { ERROR_CODE_MESSAGES, SUPPORTED_CLASSIFICATION_TYPES } from '../../constants'
import { defaultDropzoneConfig } from './constants'
import { FileError, FileUploadContextValue } from './types'
import { duplicateFileValidator } from './utils'

export const FileUploadContext = createContext<FileUploadContextValue | null>(null)

type Props = {
  children: ReactNode
}

export function FileUploadContextProvider({ children }: Props) {
  const { api } = useRootContext()
  const { selectedDocumentType } = useDocumentsFlowContext()
  const directTask = useDirectTask()

  const [trackedFiles, setTrackedFiles] = useState<TrackedFile[]>([])
  const [errors, setErrors] = useState<FileError[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [showDropzone, setShowDropzone] = useState(false)

  const shouldClassifyFile = selectedDocumentType === DocumentTypeOption.PAYSTUB_OR_W2

  const dropZoneConfig: DropzoneOptions = {
    ...defaultDropzoneConfig,
    validator: (file) => duplicateFileValidator({ file, files: trackedFiles }),
    onDragEnter: () => setDragActive(true),
    onDropAccepted: (acceptedFiles) => {
      setDragActive(false)
      handleDropAccepted(acceptedFiles)
    },
    onDropRejected: (fileRejections) => {
      setDragActive(false)
      handleDropRejected(fileRejections)
    },
  }

  const { completedFiles, failedFiles, processingFiles } = useMemo(
    () =>
      trackedFiles.reduce(
        (
          acc: {
            completedFiles: TrackedFile[]
            failedFiles: TrackedFile[]
            processingFiles: TrackedFile[]
          },
          file
        ) => {
          const completedUploading = file.state === FileUploadState.COMPLETED
          const hasSupportedClassification =
            file.classificationResults &&
            file.classificationResults.some((cr) => SUPPORTED_CLASSIFICATION_TYPES.includes(cr))
          if (completedUploading && hasSupportedClassification) {
            acc.completedFiles.push(file)
          } else if (file.state === FileUploadState.FAILED) {
            acc.failedFiles.push(file)
          } else if (file.state === FileUploadState.PROCESSING) {
            acc.processingFiles.push(file)
          }
          return acc
        },
        { completedFiles: [], failedFiles: [], processingFiles: [] }
      ),
    [trackedFiles]
  )

  function handleDropRejected(fileRejections: FileRejection[]) {
    const allErrors = fileRejections.flatMap((rejection) =>
      rejection.errors.map((error) => ({
        code: error.code as ErrorCode,
        message: ERROR_CODE_MESSAGES[error.code as ErrorCode] || error.message,
        fileName: error.code === ErrorCode.TOO_MANY_FILES ? undefined : rejection.file.name,
      }))
    )

    const tooManyFilesErrors = Array.from(
      new Map(allErrors.filter((e) => e.code === ErrorCode.TOO_MANY_FILES).map((err) => [err.code, err])).values()
    )

    const otherErrors = allErrors.filter((e) => e.code !== ErrorCode.TOO_MANY_FILES)

    if (tooManyFilesErrors.length > 0) {
      setErrors(tooManyFilesErrors)
    } else {
      setErrors([...tooManyFilesErrors, ...otherErrors])
    }
  }

  function handleDropAccepted(acceptedFiles: File[]) {
    // Map accepted files into TrackedFiles
    const newTrackedFiles = acceptedFiles.map((file) => ({
      tempId: unikId(),
      state: FileUploadState.PROCESSING,
      file,
      isRetryable: false,
    }))

    // Update TrackedFile
    setTrackedFiles((prev) => [...prev, ...newTrackedFiles])

    // Upload them
    newTrackedFiles.forEach((file) => handleUploadFile(file))
  }

  function handleUploadFailed({
    tempId,
    error,
    isRetryable,
  }: {
    tempId: string
    error?: UploadFileError['file']
    isRetryable: boolean
  }) {
    setTrackedFiles((prev) =>
      prev.map((file) => {
        if (file.tempId === tempId) {
          return {
            ...file,
            state: FileUploadState.FAILED,
            error,
            isRetryable,
          }
        }

        return file
      })
    )
  }

  function handleUploadCompleted({ tempId, data }: { tempId: string; data: UploadAndClassifyFileResponse }) {
    setTrackedFiles((prev) =>
      prev.map((file) => {
        if (file.tempId === tempId) {
          return {
            ...file,
            state: FileUploadState.COMPLETED,
            classificationResult: getFirstClassificationResult(data),
            classificationResults: data.classifications.map((cr) => cr.form_type),
            uploadedData: data,
          }
        }

        return file
      })
    )
  }

  async function handleUploadFile(trackedFile: TrackedFile) {
    if (!trackedFile?.file) throw new Error('File not found')
    if (!directTask?.id) throw new Error('No task found')

    const tempId = trackedFile?.tempId

    try {
      const formData = new FormData()
      formData.append('file', trackedFile.file)
      formData.append('type', FileType.CONSUMER_DOCUMENT)
      formData.append('skip_auto_classification', String(!shouldClassifyFile))

      // Grab the data, error and raw response from the upload and classify endpoint
      const { data, error, response } = await api.tasks.uploadAndClassifyFile(directTask.id, formData)

      if (error) {
        // If we get an error response back, we want to display the error from the backend to the user. We
        // only want to retry 5xx errors as 4xx errors are bad requests such as file validation issues
        const isRetryable = response.status >= 500
        handleUploadFailed({ tempId, error: [error.error.message], isRetryable })
      } else if (data) {
        handleUploadCompleted({ tempId, data })
      } else {
        throw new Error('Failed to upload file')
      }
    } catch (err) {
      handleUploadFailed({ tempId, error: ['Failed to upload file'], isRetryable: true })
    }
  }

  function retryUploadFile(trackedFile: TrackedFile) {
    setTrackedFiles((prev) =>
      prev.map((file) => {
        if (file.tempId === trackedFile.tempId) {
          return {
            ...file,
            state: FileUploadState.PROCESSING,
            error: undefined,
          }
        }
        return file
      })
    )

    handleUploadFile(trackedFile)
  }

  async function deleteFile({ tempId, fileId }: { tempId: string; fileId?: string }) {
    if (fileId) {
      if (!directTask?.id) throw new Error('No task found')

      try {
        setTrackedFiles((prev) => prev.filter((file) => file.uploadedData?.uploaded_file_id !== fileId))
      } catch {
        setErrors((prev) => [...prev, { code: ErrorCode.OTHER, message: 'Failed to delete file' }])
      }
    } else {
      setTrackedFiles((prev) => prev.filter((file) => file.tempId !== tempId))
    }
  }

  function updateClassificationType(tempId: string, type: ClassificationResult) {
    setTrackedFiles((prev) =>
      prev.map((file) => {
        if (file.tempId === tempId) {
          return {
            ...file,
            type,
          }
        }
        return file
      })
    )
  }

  function clearErrors() {
    setErrors([])
  }

  const dropzoneState = useDropzone(dropZoneConfig)

  return (
    <FileUploadContext.Provider
      value={{
        dropzone: dropzoneState,
        trackedFiles,
        completedFiles,
        failedFiles,
        processingFiles,
        errors,
        dragActive,
        showDropzone,
        retryUploadFile,
        deleteFile,
        updateClassificationType,
        clearErrors,
        setDragActive,
        setShowDropzone,
        setTrackedFiles,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  )
}

export function FileUploadLayout() {
  return (
    <FileUploadContextProvider>
      <Outlet />
    </FileUploadContextProvider>
  )
}

export function useFileUploadContext() {
  const context = useContext(FileUploadContext)

  if (!context) {
    throw new Error('useFileUploadContext must be used within a FileUploadContextProvider')
  }

  return context
}
